import React, { useState, useContext } from "react";
import { Link, NavLink } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import logo from "../assets/Copilot_20260817_122250.png";

const navLinkClass = ({ isActive }) => (isActive ? "active" : undefined);

export default function Navbar() {
  const { user, logout } = useContext(AuthContext);
  const { cart } = useCart();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Calculate total number of items in the cart
  const cartCount = Array.isArray(cart)
    ? cart.reduce((sum, item) => sum + (item.quantity || 1), 0)
    : 0;

  // Toggle the sidebar
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  // Close the sidebar when a link is clicked
  const closeSidebar = () => setIsSidebarOpen(false);

  return (
    <>
      {/* --- Embedded CSS for Sidebar, Cart Badge & Responsiveness --- */}
      <style>{`
        /* Desktop Menu */
        .desktop-menu {
          display: flex;
          align-items: center;
          list-style: none;
          margin: 0;
          padding: 0;
        }
        
        /* Mobile Actions Wrapper */
        .mobile-actions {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .hamburger-btn {
          display: none;
          background: none;
          border: none;
          cursor: pointer;
          padding: 10px;
          z-index: 1050;
        }

        .mobile-cart-btn {
          display: none !important; 
        }
        
        /* Mobile Sidebar Styles */
        .mobile-sidebar {
          position: fixed;
          top: 0;
          right: -300px;
          width: 280px;
          height: 100vh;
          background-color: #ffffff;
          box-shadow: -2px 0 10px rgba(0,0,0,0.1);
          transition: right 0.3s ease-in-out;
          z-index: 1050;
          padding: 60px 20px 20px 20px;
          display: flex;
          flex-direction: column;
        }
        .mobile-sidebar.open {
          right: 0;
        }
        .mobile-sidebar ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .mobile-sidebar a, .mobile-sidebar button {
          font-size: 1.2rem;
          text-decoration: none;
          color: #333;
        }
        
        /* Overlay Backdrop */
        .sidebar-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background-color: rgba(0,0,0,0.5);
          z-index: 1040;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease-in-out;
        }
        .sidebar-overlay.open {
          opacity: 1;
          pointer-events: auto;
        }

        /* Close Button inside Sidebar */
        .close-btn {
          position: absolute;
          top: 20px;
          right: 20px;
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
        }

        /* --- Professional Cart Badge Styling --- */
        .cart-icon-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          color: inherit;
          padding: 4px;
        }
        .cart-icon {
          font-size: 1.4rem;
          transition: transform 0.2s ease;
        }
        .cart-icon-btn:hover .cart-icon {
          transform: scale(1.1);
        }
        .cart-badge {
          position: absolute;
          top: -4px;
          right: -8px;
          background-color: #dc2626;
          color: #ffffff;
          font-size: 0.7rem;
          font-weight: 700;
          min-width: 18px;
          height: 18px;
          border-radius: 9999px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 4px;
          line-height: 1;
          box-shadow: 0 2px 5px rgba(0,0,0,0.2);
          border: 1.5px solid #ffffff;
        }

        /* Media Query for Mobile */
        @media (max-width: 768px) {
          .desktop-menu {
            display: none !important;
          }
          .hamburger-btn {
            display: block;
          }
          .mobile-cart-btn {
            display: inline-flex !important;
          }
        }
      `}</style>

      {/* --- Main Navbar --- */}
      <nav
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Link
          className="logo"
          to="/"
          onClick={closeSidebar}
          style={{ display: "flex", alignItems: "center" }}
        >
          <img
            src={logo}
            alt="BeeLuxe Logo"
            style={{
              height: "150px",
              width: "auto",
            }}
          />
        </Link>

        {/* Desktop Menu */}
        <ul className="desktop-menu">
          <li>
            <NavLink to="/" className={navLinkClass}>
              HOME
            </NavLink>
          </li>
          <li>
            <NavLink to="/story" className={navLinkClass}>
              OUR STORY
            </NavLink>
          </li>
          <li>
            <NavLink to="/shop" className={navLinkClass}>
              SHOP
            </NavLink>
          </li>
          <li className="border-start border-end border-light-subtle px-3 mx-3 text-muted small fw-bold">
            {user ? (
              <button
                className="btn btn-link text-decoration-none p-0"
                onClick={logout}
              >
                Logout
              </button>
            ) : (
              <NavLink to="/login" className={navLinkClass}>
                Login
              </NavLink>
            )}
          </li>
          <li>
            <NavLink to="/cart" className="cart-icon-btn">
              <i className="bi bi-cart cart-icon"></i>
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </NavLink>
          </li>
          {user ? (
            <li>
              <NavLink to="/products" className={navLinkClass}>
                Add Products
              </NavLink>
            </li>
          ) : null}
        </ul>

        {/* Mobile Actions: Cart & Hamburger grouped together */}
        <div className="mobile-actions">
          <NavLink
            to="/cart"
            className="cart-icon-btn mobile-cart-btn"
            onClick={closeSidebar}
          >
            <i className="bi bi-cart cart-icon"></i>
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </NavLink>

          {/* Hamburger Menu Button (Mobile Only) */}
          <button className="hamburger-btn" onClick={toggleSidebar}>
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white" /* Note: Make sure stroke color matches your theme if the background is light */
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
        </div>
      </nav>

      {/* --- Mobile Sidebar Overlay --- */}
      <div
        className={`sidebar-overlay ${isSidebarOpen ? "open" : ""}`}
        onClick={closeSidebar}
      ></div>

      {/* --- Mobile Sidebar Content --- */}
      <div className={`mobile-sidebar ${isSidebarOpen ? "open" : ""}`}>
        <button className="close-btn" onClick={closeSidebar}>
          &times;
        </button>
        <ul>
          <li>
            <NavLink to="/" className={navLinkClass} onClick={closeSidebar}>
              HOME
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/story"
              className={navLinkClass}
              onClick={closeSidebar}
            >
              OUR STORY
            </NavLink>
          </li>
          <li>
            <NavLink to="/shop" className={navLinkClass} onClick={closeSidebar}>
              SHOP
            </NavLink>
          </li>
          <li>
            {user ? (
              <button
                className="btn btn-link text-decoration-none p-0"
                onClick={() => {
                  logout();
                  closeSidebar();
                }}
              >
                Logout
              </button>
            ) : (
              <NavLink
                to="/login"
                className={navLinkClass}
                onClick={closeSidebar}
              >
                Login
              </NavLink>
            )}
          </li>

          {user ? (
            <li>
              <NavLink
                to="/products"
                className={navLinkClass}
                onClick={closeSidebar}
              >
                Add Products
              </NavLink>
            </li>
          ) : null}
        </ul>
      </div>
    </>
  );
}
